import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kopsAPI, REQ_STATUS_LABEL, REQ_TRANSITIONS, REQ_STATUS_ORDER } from "./kitchenApi";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Save, ClipboardList, ChevronRight, ChevronDown,
  ArrowRight, CheckCircle2, XCircle, PauseCircle,
} from "lucide-react";
import { cls, Fld, Modal, Footer } from "./KitchenPantry";
import { NumberInput } from "@/components/ui/number-input";

const STATUS_STYLE = {
  requested:        "bg-slate-100 text-slate-700",
  reviewing:        "bg-sky-100 text-sky-700",
  waiting:          "bg-amber-100 text-amber-700",
  denied:           "bg-rose-100 text-rose-700",
  approved:         "bg-emerald-100 text-emerald-700",
  picking:          "bg-violet-100 text-violet-700",
  ready:            "bg-cyan-100 text-cyan-700",
  delivered:        "bg-teal-100 text-teal-700",
  kitchen_received: "bg-emerald-200 text-emerald-800",
  closed:           "bg-slate-200 text-slate-600",
};

const STATUS_TABS = [
  { k: "",                 label: "All" },
  { k: "requested",        label: "New" },
  { k: "reviewing",        label: "Reviewing" },
  { k: "waiting",          label: "Waiting" },
  { k: "approved",         label: "Approved" },
  { k: "picking",          label: "Picking" },
  { k: "ready",            label: "Ready" },
  { k: "delivered",        label: "Delivered" },
  { k: "kitchen_received", label: "Received" },
  { k: "denied",           label: "Denied" },
  { k: "closed",           label: "Closed" },
];

export default function PantryRequests() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("");
  const [pantry, setPantry] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = () => kopsAPI.requests({ status: tab || undefined, limit: 300 })
                        .then(setRows).catch(()=>{});
  useEffect(() => { load(); }, [tab]);  // eslint-disable-line
  useEffect(() => { kopsAPI.pantry().then(setPantry); }, []);
  useEffect(() => {
    if (sp.get("new") === "1") { setCreating(true); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const counts = useMemo(() => {
    const m = {}; rows.forEach(r => { m[r.status] = (m[r.status]||0) + 1; });
    return m;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="kops-requests">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 overflow-x-auto" data-testid="kops-req-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.k} onClick={()=>setTab(t.k)}
                    data-testid={`kops-req-tab-${t.k || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border transition whitespace-nowrap ${
                      tab===t.k
                        ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}{t.k && counts[t.k] ? ` (${counts[t.k]})` : ""}
            </button>
          ))}
        </div>
        <button onClick={()=>setCreating(true)} data-testid="kops-req-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Request
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
            <div className="text-[13px] text-slate-500">No pantry requests yet.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-center px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Requested By</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Advance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <RequestRow key={r.id} r={r} open={openId===r.id}
                            onToggle={()=>setOpenId(openId===r.id ? null : r.id)}
                            onChanged={load}/>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <CreateRequestDialog pantry={pantry}
                              onClose={()=>setCreating(false)}
                              onDone={()=>{ setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function RequestRow({ r, open, onToggle, onChanged }) {
  const allowed = REQ_TRANSITIONS[r.status] || [];
  const [ask, setAsk] = useState(null); // {status, reason?, delivered_lines?}
  const summary = (r.lines || []).map(l => l.item_name).slice(0, 3).join(", ") +
                   ((r.lines || []).length > 3 ? ` +${r.lines.length - 3}` : "");
  return (
    <>
      <tr className="hover:bg-slate-50/50" data-testid={`kops-req-row-${r.id}`}>
        <td className="px-4 py-3">
          <button onClick={onToggle} data-testid={`kops-req-toggle-${r.id}`} className="p-1 rounded hover:bg-slate-100">
            {open ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
          </button>
        </td>
        <td className="px-4 py-3 mono font-medium text-hocp-navy">{r.code}</td>
        <td className="px-4 py-3 text-slate-700 truncate max-w-xs">{summary}</td>
        <td className="px-4 py-3 text-center">
          {r.priority === "urgent"
            ? <span className="px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 text-[11px] font-medium">Urgent</span>
            : <span className="text-[11px] text-slate-400 uppercase">Normal</span>}
        </td>
        <td className="px-4 py-3 text-[12px] text-slate-500">{r.created_by_email}</td>
        <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(r.created_at)}</td>
        <td className="px-4 py-3 text-center">
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLE[r.status]}`}
                data-testid={`kops-req-status-${r.id}`}>
            {REQ_STATUS_LABEL[r.status] || r.status}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-1 flex-wrap">
            {allowed.map(s => {
              const needsReason = s === "waiting" || s === "denied";
              const needsDeliv  = s === "delivered";
              const label = REQ_STATUS_LABEL[s] || s;
              const tone =
                s === "denied"           ? "bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-200" :
                s === "waiting"          ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200" :
                s === "approved"         ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200" :
                s === "kitchen_received" ? "bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border-cyan-200" :
                                            "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200";
              return (
                <button key={s}
                        onClick={()=> {
                          if (needsReason || needsDeliv) setAsk({ status: s });
                          else transition(r, s, {}, onChanged);
                        }}
                        data-testid={`kops-req-action-${r.id}-${s}`}
                        className={`h-7 px-2 rounded-md border text-[11.5px] font-medium inline-flex items-center gap-1 ${tone}`}>
                  {s === "denied" && <XCircle className="w-3 h-3"/>}
                  {s === "waiting" && <PauseCircle className="w-3 h-3"/>}
                  {s === "approved" && <CheckCircle2 className="w-3 h-3"/>}
                  {!["denied","waiting","approved"].includes(s) && <ArrowRight className="w-3 h-3"/>}
                  {label}
                </button>
              );
            })}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/50">
          <td colSpan={8} className="px-4 py-4">
            <RequestDetail r={r}/>
          </td>
        </tr>
      )}
      {ask && (
        <ActionDialog
          request={r} status={ask.status}
          onClose={()=>setAsk(null)}
          onDone={payload => { setAsk(null); transition(r, ask.status, payload, onChanged); }}
        />
      )}
    </>
  );
}

async function transition(r, status, payload, onChanged) {
  try {
    await kopsAPI.updateRequestStatus(r.id, { status, ...payload });
    toast.success(`Request → ${REQ_STATUS_LABEL[status] || status}`);
    onChanged();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed");
  }
}

function RequestDetail({ r }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: Lines */}
      <div className="lg:col-span-2">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-500 text-[10.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-right px-3 py-2">Requested</th>
                <th className="text-right px-3 py-2">Kitchen Stock</th>
                <th className="text-right px-3 py-2">Min/Max</th>
                <th className="text-right px-3 py-2">Last Delivered</th>
                <th className="text-right px-3 py-2">ADU</th>
                <th className="text-right px-3 py-2">Days Left</th>
                <th className="text-right px-3 py-2">Open Reqs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(r.lines || []).map((L, i) => (
                <tr key={i} data-testid={`kops-req-line-${r.id}-${i}`}>
                  <td className="px-3 py-2 font-medium text-hocp-navy">{L.item_name}</td>
                  <td className="px-3 py-2 text-right mono font-semibold tabular-nums">
                    {L.qty} {L.unit}
                    {L.delivered_qty && L.delivered_qty !== L.qty && (
                      <div className="text-[10.5px] text-teal-600">delivered {L.delivered_qty}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right mono tabular-nums text-slate-700">{L.kitchen_current_stock}</td>
                  <td className="px-3 py-2 text-right mono tabular-nums text-slate-500">
                    {L.kitchen_min_stock} / {L.kitchen_max_stock}
                  </td>
                  <td className="px-3 py-2 text-right mono tabular-nums text-slate-500">
                    {L.last_delivered_qty || "—"}
                    {L.last_delivered_date && <div className="text-[10.5px] text-slate-400">{fmtDT(L.last_delivered_date)}</div>}
                  </td>
                  <td className="px-3 py-2 text-right mono tabular-nums text-slate-500">{L.avg_daily_usage}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {L.days_remaining >= 999 ? "—" : `${L.days_remaining}d`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{L.open_requests_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {r.reason && (
          <div className="mt-2 text-[12px] text-slate-500">
            <span className="font-medium">Reason:</span> {r.reason}
          </div>
        )}
      </div>

      {/* Right: Timeline */}
      <div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Timeline</div>
          <ol className="space-y-3">
            {(r.history || []).map((h, i) => (
              <li key={i} className="flex gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${STATUS_STYLE[h.status]?.split(" ")[0].replace("bg-", "bg-") || "bg-slate-400"}`}/>
                <div className="flex-1">
                  <div className="text-[12.5px] font-medium text-hocp-navy capitalize">
                    {REQ_STATUS_LABEL[h.status] || h.status}
                  </div>
                  <div className="text-[11px] text-slate-500 mono">{fmtDT(h.at)}</div>
                  {h.actor && <div className="text-[11px] text-slate-400 truncate">by {h.actor}</div>}
                  {h.reason && <div className="text-[11px] text-slate-500 italic">&quot;{h.reason}&quot;</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function ActionDialog({ request, status, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [deliveredMap, setDeliveredMap] = useState(() => {
    const m = {}; (request.lines || []).forEach(L => { m[L.pantry_item_id] = L.qty; });
    return m;
  });
  const isReason = status === "waiting" || status === "denied";
  const isDeliver = status === "delivered";

  const submit = () => {
    if (isReason && !reason.trim()) { toast.error(`Reason required for ${status}`); return; }
    const payload = {};
    if (isReason) payload.reason = reason.trim();
    if (isDeliver) {
      payload.delivered_lines = (request.lines || []).map(L => ({
        pantry_item_id: L.pantry_item_id,
        delivered_qty:  deliveredMap[L.pantry_item_id] || L.qty,
      }));
    }
    onDone(payload);
  };

  return (
    <Modal title={`${REQ_STATUS_LABEL[status] || status} · ${request.code}`} onClose={onClose}>
      {isReason && (
        <Fld label={`Reason (required for ${status})`} full>
          <textarea value={reason} onChange={e=>setReason(e.target.value)}
                    data-testid={`kops-req-reason-${status}`} rows={3}
                    className={cls + " h-auto py-2 resize-y"}
                    placeholder="Explain why this request is waiting / denied…"/>
        </Fld>
      )}
      {isDeliver && (
        <div className="space-y-2">
          <div className="text-[11.5px] text-slate-500 font-medium">Confirm delivered quantities</div>
          {(request.lines || []).map(L => (
            <div key={L.pantry_item_id} className="flex items-center gap-3">
              <div className="flex-1 text-[13px] font-medium text-hocp-navy">{L.item_name}</div>
              <div className="text-[11.5px] text-slate-400 mono">requested {L.qty} {L.unit}</div>
              <NumberInput step="0.001" value={deliveredMap[L.pantry_item_id] || ""}
                     data-testid={`kops-req-delivered-${L.pantry_item_id}`}
                     onChange={e=>setDeliveredMap({ ...deliveredMap, [L.pantry_item_id]: e.target.value })}
                     className={cls + " w-32"}/>
              <div className="text-[11.5px] text-slate-500 w-8">{L.unit}</div>
            </div>
          ))}
        </div>
      )}
      <Footer onClose={onClose} onSubmit={submit} testid={`kops-req-confirm-${status}`}/>
    </Modal>
  );
}

function CreateRequestDialog({ pantry, onClose, onDone }) {
  const [lines, setLines] = useState([{ pantry_item_id: "", item_name: "", qty: "", unit: "kg" }]);
  const [reason, setReason] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  const setLine = (i, patch) => {
    const next = [...lines]; next[i] = { ...next[i], ...patch }; setLines(next);
  };
  const pickItem = (i, id) => {
    const p = pantry.find(x => x.id === id);
    if (!p) return;
    setLine(i, { pantry_item_id: p.id, item_name: p.item_name, unit: p.unit });
  };
  const removeLine = i => setLines(lines.filter((_, idx) => idx !== i));
  const addLine    = () => setLines([...lines, { pantry_item_id: "", item_name: "", qty: "", unit: "kg" }]);

  const submit = async () => {
    const clean = lines.filter(l => l.pantry_item_id && Number(l.qty) > 0);
    if (!clean.length) { toast.error("Add at least one item with quantity > 0"); return; }
    setSaving(true);
    try {
      await kopsAPI.createRequest({ lines: clean, reason: reason.trim() || null, priority });
      toast.success("Pantry request submitted"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Pantry Request" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Priority">
            <select value={priority} onChange={e=>setPriority(e.target.value)} data-testid="kops-req-priority" className={cls}>
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
            </select>
          </Fld>
          <Fld label="Reason (optional)">
            <input value={reason} onChange={e=>setReason(e.target.value)}
                   placeholder="e.g. Dinner service prep" data-testid="kops-req-reason" className={cls}/>
          </Fld>
        </div>
        <div className="space-y-2">
          <div className="text-[11.5px] text-slate-500 font-medium">Items</div>
          {lines.map((L, i) => {
            const p = pantry.find(x => x.id === L.pantry_item_id);
            return (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`kops-req-line-${i}`}>
                <select value={L.pantry_item_id} onChange={e=>pickItem(i, e.target.value)}
                        data-testid={`kops-req-item-${i}`} aria-label="Ingredient" className={cls + " col-span-6"}>
                  <option value="">Select ingredient…</option>
                  {pantry.map(x => <option key={x.id} value={x.id}>{x.item_name} — {x.current_stock} {x.unit}</option>)}
                </select>
                <NumberInput step="0.001" placeholder="Qty" aria-label="Quantity" value={L.qty}
                       onChange={e=>setLine(i, { qty: e.target.value })}
                       data-testid={`kops-req-qty-${i}`} className={cls + " col-span-2"}/>
                <div className={cls + " col-span-1 flex items-center justify-center text-slate-500 uppercase text-[11px]"}>{L.unit}</div>
                <div className={cls + " col-span-2 flex items-center justify-end gap-2 mono tabular-nums text-[11px] text-slate-500"}>
                  {p ? `${p.current_stock} on hand` : "—"}
                </div>
                <button onClick={()=>removeLine(i)} disabled={lines.length===1}
                        data-testid={`kops-req-line-remove-${i}`}
                        className="col-span-1 h-10 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center disabled:opacity-30">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            );
          })}
          <button onClick={addLine} data-testid="kops-req-add-line"
                  className="h-9 px-3 rounded-lg border border-dashed border-slate-300 text-[12.5px] text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2">
            <Plus className="w-3.5 h-3.5"/> Add another item
          </button>
        </div>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="kops-req-submit"/>
    </Modal>
  );
}

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString([], { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }); }
  catch { return "—"; }
}
